import React, { useEffect, useState, useCallback } from 'react';
import {
    Badge,
    Box,
    Tabs,
    Tab,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Snackbar,
    Autocomplete,
    Paper,
    useTheme,
    alpha,
    IconButton,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { styled } from '@mui/system';
import type { Theme } from '@mui/material/styles';
import { DataGridPremium, GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium';
import InventoryIcon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GavelIcon from '@mui/icons-material/Gavel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BusinessIcon from '@mui/icons-material/Business';
import BugReportIcon from '@mui/icons-material/BugReport';
import {
    getFp1Devices,
    getFp1DeviceStatusCounts,
    getFp1DeviceSubscriptionStatusCounts,
    getFp1Shipments,
    type GetDevicesParams,
    type GetShipmentsParams,
} from '../fp1.api';
import Fp1ReturnsTab from '../components/Fp1ReturnsTab';
import Fp1ContractsTab from '../components/Fp1ContractsTab';
import Fp1ManagementTab from '../components/Fp1ManagementTab';
import Fp1OrgPodsTab from '../components/Fp1OrgPodsTab';
import {
    Fp1Device,
    Fp1Shipment,
    Fp1DeviceStatusCounts,
    Fp1DeviceStatus,
    formatFp1SubscriptionStatusLabel,
    fp1SubscriptionStatusColor,
    fp1EffectiveAthleteName,
    fp1EffectiveAthleteUuid,
    fp1StripeSubscriptionNeedsAttention,
} from '../fp1.types';
import Fp1DeviceDetailDialog from '../components/Fp1DeviceDetailDialog';
import Fp1ReceiveShipmentDialog from '../components/Fp1ReceiveShipmentDialog';
import Fp1ShipmentDetailDialog from '../components/Fp1ShipmentDetailDialog';
import Fp1SubTrackingTab from '../components/Fp1SubTrackingTab';
import Fp1BatteryHealthTab from '../components/Fp1BatteryHealthTab';
import Fp1DebuggingTab from '../components/Fp1DebuggingTab';
import { Organization } from 'utils';
import { adminGetAllOrganizations } from '../../../admin.organizations.api';
import { debounce } from 'lodash';
import Fp1OrdersSection from './Fp1OrdersSection';
import { UseAdminStatus } from '../../../../../contexts/AdminContext';

// ── Styled ───────────────────────────────────────────────────────────────

const FilterBar = styled('form')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
}));

const SubTab = styled(Tab)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.85rem',
    minHeight: 42,
    '& .MuiTab-iconWrapper': {
        marginBottom: 0,
        marginRight: theme.spacing(0.75),
    },
}));

// ── Helpers ──────────────────────────────────────────────────────────────

const formatStatus = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const formatDate = (d: string | null) =>
    d
        ? new Date(d).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : '--';
const formatCost = (cents: number | null) =>
    cents != null ? `$${(cents / 100).toFixed(2)}` : '--';

const DEVICE_STATUS_COLORS: Record<
    Fp1DeviceStatus,
    'success' | 'info' | 'warning' | 'error' | 'default'
> = {
    in_stock: 'success',
    deployed: 'info',
    maintenance: 'warning',
    defective: 'error',
    unknown: 'default',
    returned: 'default',
    decommissioned: 'default',
};

const EMPTY_DEVICE_STATUS_COUNTS: Fp1DeviceStatusCounts = {
    total: 0,
    in_stock: 0,
    deployed: 0,
    maintenance: 0,
    defective: 0,
    unknown: 0,
    returned: 0,
    decommissioned: 0,
};

// Note: "Returned" is intentionally omitted from the Devices subtab — returns are
// tracked on the dedicated Returns tab. Hidden here per product decision.
const DEVICE_STATUS_SUMMARY: { key: Fp1DeviceStatus; label: string }[] = [
    { key: 'in_stock', label: 'In Stock' },
    { key: 'deployed', label: 'Deployed' },
    { key: 'maintenance', label: 'Maintenance' },
    { key: 'defective', label: 'Defective' },
    { key: 'unknown', label: 'Unknown' },
    { key: 'decommissioned', label: 'Decommissioned' },
];

// ── Optional (toggleable) Devices-grid columns ─────────────────────────────
// These are hidden by default and only appear once the admin toggles them on AND
// clicks Search. The always-on default columns are everything NOT listed here.
const OPTIONAL_DEVICE_COLUMNS: { field: string; label: string }[] = [
    { field: 'subs_vs_pods', label: 'Subs / Pods' },
    { field: 'usage_count_30d', label: 'Used (30d)' },
    { field: 'last_used_at', label: 'Last Used' },
    { field: 'hardware_version', label: 'Hardware (PCB)' },
    { field: 'firmware_version', label: 'Firmware' },
];
const OPTIONAL_DEVICE_COLUMN_FIELDS = OPTIONAL_DEVICE_COLUMNS.map((c) => c.field);
const OPTIONAL_COLS_STORAGE_KEY = 'fp1.devices.optionalColumns';

const loadOptionalCols = (): string[] => {
    try {
        const raw = localStorage.getItem(OPTIONAL_COLS_STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                return parsed.filter((f) => OPTIONAL_DEVICE_COLUMN_FIELDS.includes(f));
            }
        }
    } catch {
        // ignore malformed/unavailable storage
    }
    return [];
};

// Label an org for the Devices org filter as "Name (Tier)" so admins can see at a
// glance which orgs have an active subscription tier. Orgs with no active tier show
// "(No Tier)". When the tier string is absent (e.g. an org injected via the Org Pods
// deep-link, which carries only uuid + name), fall back to the bare name.
const orgOptionLabel = (o: Organization): string => {
    switch (o.subscription_tier_string) {
        case 'gold':
            return `${o.name} (Gold)`;
        case 'silver':
            return `${o.name} (Silver)`;
        case 'bronze':
            return `${o.name} (Bronze)`;
        case 'no_tier':
            return `${o.name} (No Tier)`;
        default:
            return o.name;
    }
};

const deviceStatusSummaryAccent = (theme: Theme, status: Fp1DeviceStatus): string => {
    switch (status) {
        case 'in_stock':
            return theme.palette.success.main;
        case 'deployed':
            return theme.palette.info.main;
        case 'maintenance':
            return theme.palette.warning.main;
        case 'defective':
            return theme.palette.error.main;
        case 'unknown':
            return theme.palette.text.secondary;
        case 'returned':
            return theme.palette.text.secondary;
        case 'decommissioned':
            return theme.palette.grey[600];
        default:
            return theme.palette.primary.main;
    }
};

// ── Summary Cards ────────────────────────────────────────────────────────

interface SummaryCardProps {
    label: string;
    value: string | number;
    color?: string;
    icon?: React.ReactNode;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, color, icon }) => (
    <Paper
        variant="outlined"
        sx={{
            px: 2.5,
            py: 1.5,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minWidth: 140,
            borderColor: color ? alpha(color, 0.3) : 'divider',
            bgcolor: color ? alpha(color, 0.04) : 'transparent',
        }}
    >
        {icon}
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
        </Box>
    </Paper>
);

// ═════════════════════════════════════════════════════════════════════════
// ── Main Component ───────────────────────────────────────────────────────
// ═════════════════════════════════════════════════════════════════════════

const Fp1Page = () => {
    const theme = useTheme();
    const { pendingOrders } = UseAdminStatus();
    const [activeTab, setActiveTab] = useState(0);
    const [snackMessage, setSnackMessage] = useState('');

    // ── Devices state ────────────────────────────────────────────────────
    const [devices, setDevices] = useState<Fp1Device[]>([]);
    /** Row count for the filtered device grid only (pagination), not global inventory */
    const [deviceFilteredRowCount, setDeviceFilteredRowCount] = useState(0);
    const [deviceStatusCounts, setDeviceStatusCounts] = useState<Fp1DeviceStatusCounts>(
        EMPTY_DEVICE_STATUS_COUNTS,
    );
    const [deviceParams, setDeviceParams] = useState<GetDevicesParams>({ page: 1, per_page: 25 });
    const [devicePagination, setDevicePagination] = useState({ page: 0, pageSize: 25 });
    const [deviceSerialSearch, setDeviceSerialSearch] = useState('');
    const [deviceTypeFilter, setDeviceTypeFilter] = useState<string>('');
    const [deviceStatusFilter, setDeviceStatusFilter] = useState<string>('');
    const [deviceSubscriptionFilter, setDeviceSubscriptionFilter] = useState<string>('');
    // Revenue holder filter: '' = any, 'athlete' = Transferred (athlete pays), 'org' = org pays.
    const [deviceHolderFilter, setDeviceHolderFilter] = useState<string>('');
    const [deviceOrganizationFilter, setDeviceOrganizationFilter] = useState<string>('');
    const [deviceAthleteSearch, setDeviceAthleteSearch] = useState('');
    const [deviceSubStatusCounts, setDeviceSubStatusCounts] = useState<{
        na: number;
        comp: number;
        invoiced: number;
        active_athlete: number;
        active_org: number;
        partial_org: number;
        past_due: number;
        no_sub: number;
        excess_pods: number;
    } | null>(null);
    const [selectedDevice, setSelectedDevice] = useState<Fp1Device | null>(null);
    const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
    const [exportingDevices, setExportingDevices] = useState(false);
    // Optional column visibility — `pending` is what the toggle buttons reflect; `applied`
    // is what the grid actually shows (synced on Search), persisted to localStorage.
    const [pendingOptionalCols, setPendingOptionalCols] = useState<string[]>(loadOptionalCols);
    const [appliedOptionalCols, setAppliedOptionalCols] = useState<string[]>(loadOptionalCols);
    const optionalColsDirty =
        pendingOptionalCols.length !== appliedOptionalCols.length ||
        pendingOptionalCols.some((f) => !appliedOptionalCols.includes(f));
    // Usage telemetry is expensive on the backend, so only request it when one of the
    // usage columns (Used 30d / Last Used) is actually being shown.
    const usageRequested = (cols: string[]) =>
        cols.includes('usage_count_30d') || cols.includes('last_used_at');

    // ── Shipments state ──────────────────────────────────────────────────
    const [shipments, setShipments] = useState<Fp1Shipment[]>([]);
    const [totalShipments, setTotalShipments] = useState(0);
    const [shipmentPagination, setShipmentPagination] = useState({ page: 0, pageSize: 25 });
    const [shipmentSupplierSearch, setShipmentSupplierSearch] = useState('');
    const [receiveShipmentOpen, setReceiveShipmentOpen] = useState(false);
    const [shipmentDetailUuid, setShipmentDetailUuid] = useState<string | null>(null);
    const [shipmentDetailOpen, setShipmentDetailOpen] = useState(false);

    // (Organizations dashboard removed from this tab — use the main Organizations tab)

    // ── Orgs autocomplete ────────────────────────────────────────────────
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchOrganizations = useCallback(
        debounce((searchTerm: string) => {
            adminGetAllOrganizations(searchTerm, 500)
                .then((res) =>
                    setOrganizations(
                        res.data.data.filter((o) => !o.name.includes("'s Personal Organization")),
                    ),
                )
                .catch((err) => console.error('Failed to fetch orgs', err));
        }, 300),
        [],
    );

    useEffect(() => {
        fetchOrganizations('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Data fetching ────────────────────────────────────────────────────

    const fetchDevices = useCallback(async (params?: GetDevicesParams) => {
        try {
            const p = params || deviceParams;
            const res = await getFp1Devices(p);
            setDevices(res.data?.data ?? []);
            setDeviceFilteredRowCount(res.data?.meta?.total ?? 0);
        } catch (err) {
            console.error('Failed to fetch devices', err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchDeviceStatusCounts = useCallback(async () => {
        try {
            const res = await getFp1DeviceStatusCounts();
            const data = res.data?.data;
            if (data) {
                const sumByStatus =
                    (data.in_stock ?? 0) +
                    (data.deployed ?? 0) +
                    (data.maintenance ?? 0) +
                    (data.defective ?? 0) +
                    (data.unknown ?? 0) +
                    (data.returned ?? 0) +
                    (data.decommissioned ?? 0);
                setDeviceStatusCounts({
                    total: data.total ?? sumByStatus,
                    in_stock: data.in_stock ?? 0,
                    deployed: data.deployed ?? 0,
                    maintenance: data.maintenance ?? 0,
                    defective: data.defective ?? 0,
                    unknown: data.unknown ?? 0,
                    returned: data.returned ?? 0,
                    decommissioned: data.decommissioned ?? 0,
                });
            }
        } catch (err) {
            console.error('Failed to fetch device status counts', err);
        }
    }, []);

    const fetchDeviceSubStatusCounts = useCallback(async () => {
        try {
            const res = await getFp1DeviceSubscriptionStatusCounts();
            const d = res.data?.data;
            if (d) {
                setDeviceSubStatusCounts({
                    na: d.na ?? 0,
                    comp: d.comp ?? 0,
                    invoiced: d.invoiced ?? 0,
                    active_athlete: d.active_athlete ?? 0,
                    active_org: d.active_org ?? 0,
                    partial_org: d.partial_org ?? 0,
                    past_due: d.past_due ?? 0,
                    no_sub: d.no_sub ?? 0,
                    excess_pods: d.excess_pods ?? 0,
                });
            }
        } catch (err) {
            console.error('Failed to fetch subscription status counts', err);
        }
    }, []);

    const fetchShipments = useCallback(async (params?: GetShipmentsParams) => {
        try {
            const res = await getFp1Shipments(
                params || {
                    page: shipmentPagination.page + 1,
                    per_page: shipmentPagination.pageSize,
                    supplier_name: shipmentSupplierSearch || undefined,
                },
            );
            setShipments(res.data?.data ?? []);
            setTotalShipments(res.data?.meta?.total ?? 0);
        } catch (err) {
            console.error('Failed to fetch shipments', err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchDevices({ page: 1, per_page: 25, include_usage: usageRequested(appliedOptionalCols) });
        fetchShipments({ page: 1, per_page: 25 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeTab === 3) {
            fetchDeviceStatusCounts();
            fetchDeviceSubStatusCounts();
        }
    }, [activeTab, fetchDeviceStatusCounts, fetchDeviceSubStatusCounts]);

    // ── Device search handler ────────────────────────────────────────────

    const handleDeviceSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const params: GetDevicesParams = {
            page: 1,
            per_page: devicePagination.pageSize,
            status: deviceStatusFilter || undefined,
            subscription_status: deviceSubscriptionFilter || undefined,
            subscription_holder: deviceHolderFilter || undefined,
            device_type: deviceTypeFilter || undefined,
            organization_uuid: deviceOrganizationFilter || undefined,
            athlete_name: deviceAthleteSearch.trim() || undefined,
            serial_number: deviceSerialSearch || undefined,
            // pending becomes applied below — request usage to match the columns being shown.
            include_usage: usageRequested(pendingOptionalCols),
        };
        setDeviceParams(params);
        setDevicePagination({ ...devicePagination, page: 0 });
        fetchDevices(params);

        // Apply the pending optional-column selection and remember it for next time.
        setAppliedOptionalCols(pendingOptionalCols);
        try {
            localStorage.setItem(OPTIONAL_COLS_STORAGE_KEY, JSON.stringify(pendingOptionalCols));
        } catch {
            // ignore unavailable storage
        }
    };

    // Deep-link from the Org Pods subtab: jump to Devices, filtered to one org.
    const handleViewOrgDevices = (orgUuid: string, orgName: string) => {
        // Make sure the org autocomplete can render this selection by name.
        setOrganizations((prev) =>
            prev.some((o) => o.uuid === orgUuid)
                ? prev
                : [{ uuid: orgUuid, name: orgName } as Organization, ...prev],
        );
        // Clear the other device filters so only the org filter is in effect.
        setDeviceOrganizationFilter(orgUuid);
        setDeviceStatusFilter('');
        setDeviceSubscriptionFilter('');
        setDeviceHolderFilter('');
        setDeviceTypeFilter('');
        setDeviceAthleteSearch('');
        setDeviceSerialSearch('');
        const params: GetDevicesParams = {
            page: 1,
            per_page: devicePagination.pageSize,
            organization_uuid: orgUuid,
            include_usage: usageRequested(appliedOptionalCols),
        };
        setDeviceParams(params);
        setDevicePagination({ ...devicePagination, page: 0 });
        fetchDevices(params);
        setActiveTab(3);
    };

    // Deep-link from an org card's expanded athlete-subscription rows: jump to Devices,
    // filtered to that org's athlete-Transferred pods with the given subscription status
    // ('active_athlete' or 'past_due'). subscription_holder='athlete' keeps org-owned pods
    // out, so the grid shows exactly the athletes counted on the Orgs card.
    const handleViewOrgAthleteSubs = (
        orgUuid: string,
        orgName: string,
        status: 'active_athlete' | 'past_due',
    ) => {
        setOrganizations((prev) =>
            prev.some((o) => o.uuid === orgUuid)
                ? prev
                : [{ uuid: orgUuid, name: orgName } as Organization, ...prev],
        );
        setDeviceOrganizationFilter(orgUuid);
        setDeviceSubscriptionFilter(status);
        setDeviceHolderFilter('athlete');
        setDeviceStatusFilter('');
        setDeviceTypeFilter('');
        setDeviceAthleteSearch('');
        setDeviceSerialSearch('');
        const params: GetDevicesParams = {
            page: 1,
            per_page: devicePagination.pageSize,
            organization_uuid: orgUuid,
            subscription_status: status,
            subscription_holder: 'athlete',
            include_usage: usageRequested(appliedOptionalCols),
        };
        setDeviceParams(params);
        setDevicePagination({ ...devicePagination, page: 0 });
        fetchDevices(params);
        setActiveTab(3);
    };

    const handleDevicePaginationChange = (model: GridPaginationModel) => {
        setDevicePagination(model);
        const params = { ...deviceParams, page: model.page + 1, per_page: model.pageSize };
        setDeviceParams(params);
        fetchDevices(params);
    };

    // ── Device CSV export ────────────────────────────────────────────────
    // Fetches EVERY device matching the current filters (all pages) and writes a
    // CSV with all grid fields plus the diagnostic ones (raw subscription status,
    // org coverage, athlete assignment type, linked Stripe subscription).

    const orgNameForDevice = (row: Fp1Device): string => {
        const uuid = row.assigned_org?.uuid?.trim() || row.organization_uuid?.trim() || '';
        return (
            row.assigned_org?.name?.trim() ||
            row.organization_name?.trim() ||
            (uuid ? organizations.find((o) => o.uuid === uuid)?.name : '') ||
            ''
        );
    };

    const handleExportDevices = async () => {
        setExportingDevices(true);
        try {
            const perPage = 200;
            const baseParams: GetDevicesParams = {
                status: deviceStatusFilter || undefined,
                subscription_status: deviceSubscriptionFilter || undefined,
                device_type: deviceTypeFilter || undefined,
                organization_uuid: deviceOrganizationFilter || undefined,
                athlete_name: deviceAthleteSearch.trim() || undefined,
                serial_number: deviceSerialSearch || undefined,
                // The CSV includes Used (30d) / Last Used, so always fetch usage for the export.
                include_usage: true,
            };

            const all: Fp1Device[] = [];
            let page = 1;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                // eslint-disable-next-line no-await-in-loop
                const res = await getFp1Devices({ ...baseParams, page, per_page: perPage });
                const rows = res.data?.data ?? [];
                all.push(...rows);
                const total = res.data?.meta?.total ?? all.length;
                if (rows.length === 0 || all.length >= total || page > 200) break;
                page += 1;
            }

            const columns: { header: string; value: (r: Fp1Device) => string | number | null }[] = [
                { header: 'Serial Number', value: (r) => r.serial_number },
                { header: 'Device Type', value: (r) => r.device_type },
                { header: 'FP1 Status', value: (r) => r.status },
                {
                    header: 'Is Deployed',
                    value: (r) => (r.status === 'deployed' ? 'yes' : 'no'),
                },
                {
                    header: 'Sub Status (displayed)',
                    value: (r) =>
                        formatFp1SubscriptionStatusLabel(
                            r.subscription_status,
                            r.status === 'deployed',
                        ),
                },
                { header: 'Sub Status (raw)', value: (r) => r.subscription_status ?? '' },
                { header: 'Custody Status', value: (r) => r.custody_status ?? '' },
                { header: 'Organization', value: (r) => orgNameForDevice(r) },
                {
                    header: 'Organization UUID',
                    value: (r) => r.assigned_org?.uuid || r.organization_uuid || '',
                },
                { header: 'Org Tier', value: (r) => r.assigned_org?.tier ?? '' },
                { header: 'Org Pod Count', value: (r) => r.org_pod_count ?? '' },
                {
                    header: 'Org Subscription Seats',
                    value: (r) => r.org_subscription_quantity ?? '',
                },
                {
                    header: 'Org Is Partial',
                    value: (r) =>
                        r.org_pod_count != null &&
                        r.org_subscription_quantity != null &&
                        r.org_subscription_quantity > 0 &&
                        r.org_pod_count > r.org_subscription_quantity
                            ? 'yes'
                            : '',
                },
                {
                    header: 'Org Excess Pods',
                    value: (r) =>
                        r.org_pod_count != null &&
                        r.org_subscription_quantity != null &&
                        r.org_pod_count > r.org_subscription_quantity
                            ? r.org_pod_count - r.org_subscription_quantity
                            : '',
                },
                {
                    header: 'Athlete',
                    value: (r) =>
                        r.assigned_athlete?.name?.trim() ||
                        r.assigned_athlete?.email?.trim() ||
                        fp1EffectiveAthleteName(r) ||
                        '',
                },
                {
                    header: 'Athlete UUID',
                    value: (r) => r.assigned_athlete?.uuid || fp1EffectiveAthleteUuid(r) || '',
                },
                {
                    header: 'Athlete Assignment',
                    value: (r) => {
                        const uuid = r.assigned_athlete?.uuid || fp1EffectiveAthleteUuid(r);
                        if (uuid) {
                            // Same definition as the grid's Athlete column: a transfer to an
                            // athlete with no subscription has no linked sub id, so the
                            // explicit transfer marker counts too.
                            return r.stripe_linked_subscription?.subscription_id ||
                                r.athlete_transferred_at
                                ? 'Transferred'
                                : 'Assigned';
                        }
                        return r.pending_transfer_to?.uuid ? 'Transfer pending' : '';
                    },
                },
                {
                    header: 'Sub Status Removed At',
                    value: (r) => r.subscription_cleared_at ?? '',
                },
                {
                    header: 'Linked Sub ID',
                    value: (r) => r.stripe_linked_subscription?.subscription_id ?? '',
                },
                {
                    header: 'Linked Sub Name',
                    value: (r) => r.stripe_linked_subscription?.subscription_name ?? '',
                },
                {
                    header: 'Linked Sub Stripe Status',
                    value: (r) => r.stripe_linked_subscription?.subscription_status ?? '',
                },
                {
                    header: 'Hardware (PCB)',
                    value: (r) => r.hardware_version || r.model || '',
                },
                { header: 'Firmware', value: (r) => r.firmware_version || '' },
                { header: 'Used (30d)', value: (r) => r.usage_count_30d ?? '' },
                { header: 'Last Used', value: (r) => r.last_used_at ?? '' },
                { header: 'Decommissioned At', value: (r) => r.decommissioned_at ?? '' },
                { header: 'Pod UUID', value: (r) => r.pod_uuid ?? '' },
            ];

            const escape = (v: string | number | null): string => {
                const s = v == null ? '' : String(v);
                return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            };

            const lines = [
                columns.map((c) => escape(c.header)).join(','),
                ...all.map((r) => columns.map((c) => escape(c.value(r))).join(',')),
            ];
            const csv = lines.join('\n');

            // Prepend a UTF-8 BOM so Excel opens the file with correct encoding.
            const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const stamp = new Date().toISOString().slice(0, 10);
            a.download = `fp1-devices-${stamp}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            setSnackMessage(`Exported ${all.length} devices to CSV`);
        } catch (err) {
            console.error('Failed to export devices', err);
            setSnackMessage('Failed to export devices');
        } finally {
            setExportingDevices(false);
        }
    };

    // ── Shipment search handler ──────────────────────────────────────────

    const handleShipmentSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const params: GetShipmentsParams = {
            page: 1,
            per_page: shipmentPagination.pageSize,
            supplier_name: shipmentSupplierSearch || undefined,
        };
        setShipmentPagination({ ...shipmentPagination, page: 0 });
        fetchShipments(params);
    };

    const handleShipmentPaginationChange = (model: GridPaginationModel) => {
        setShipmentPagination(model);
        fetchShipments({
            page: model.page + 1,
            per_page: model.pageSize,
            supplier_name: shipmentSupplierSearch || undefined,
        });
    };

    // ── Column Definitions ───────────────────────────────────────────────

    const allDeviceColumns: GridColDef<Fp1Device>[] = [
        {
            field: 'serial_number',
            headerName: 'Serial Number',
            width: 210,
            renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {params.value}
                    </Typography>
                    {params.value && (
                        <Tooltip title="Copy serial number">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void navigator.clipboard.writeText(params.value as string);
                                }}
                                sx={{ p: '2px', opacity: 0.5, '&:hover': { opacity: 1 } }}
                            >
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        },
        {
            field: 'device_type',
            headerName: 'Type',
            width: 110,
            renderCell: (params) => (
                <Chip
                    label={formatStatus(params.value)}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                />
            ),
        },
        {
            field: 'status',
            headerName: 'FP1 status',
            width: 130,
            renderCell: (params) => (
                <Chip
                    label={formatStatus(params.value)}
                    size="small"
                    color={DEVICE_STATUS_COLORS[params.value as Fp1DeviceStatus]}
                    sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                />
            ),
        },
        {
            field: 'subscription_status',
            headerName: 'Sub. Status',
            width: 180,
            sortable: false,
            renderCell: (params) => {
                const row = params.row;
                const isDeployed = row.status === 'deployed';
                const label = formatFp1SubscriptionStatusLabel(row.subscription_status, isDeployed);
                const color = fp1SubscriptionStatusColor(row.subscription_status);

                return (
                    <Chip
                        label={label}
                        size="small"
                        variant={label === 'No Sub' || label === 'N/A' ? 'outlined' : 'filled'}
                        color={color}
                        sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                    />
                );
            },
        },
        {
            // Optional column: org pod-coverage "X/Y" (pods / paid seats). Flags partial
            // orgs (more org-covered pods than paid seats). Athlete/comp/warehouse rows
            // have no org coverage and render "--".
            field: 'subs_vs_pods',
            headerName: 'Subs / Pods',
            width: 150,
            sortable: false,
            renderCell: (params) => {
                const row = params.row;
                const podCount = row.org_pod_count;
                const subQty = row.org_subscription_quantity;
                const showCoverage = podCount != null && subQty != null && subQty > 0;
                if (!showCoverage) {
                    return (
                        <Typography variant="body2" color="text.secondary">
                            --
                        </Typography>
                    );
                }
                const isOver = (podCount as number) > (subQty as number);
                return (
                    <Chip
                        label={isOver ? `Partial (${podCount}/${subQty})` : `${podCount}/${subQty}`}
                        size="small"
                        variant="outlined"
                        color={isOver ? 'warning' : 'success'}
                        sx={{ fontWeight: 500, fontSize: '0.72rem', height: 22 }}
                        title={
                            isOver
                                ? 'Org-covered pods exceed paid subscription seats'
                                : 'Org-covered pods / paid subscription seats'
                        }
                    />
                );
            },
        },
        {
            field: 'organization_uuid',
            headerName: 'Organization',
            width: 230,
            sortable: false,
            renderCell: (params) => {
                const row = params.row;
                const uuid =
                    row.assigned_org?.uuid?.trim() || row.organization_uuid?.trim() || null;
                if (!uuid) {
                    return (
                        <Typography variant="body2" color="text.secondary">
                            --
                        </Typography>
                    );
                }
                const fromHierarchy = row.assigned_org?.name?.trim() || null;
                const fromApi = row.organization_name?.trim() || null;
                const fromList = organizations.find((o) => o.uuid === uuid)?.name;
                const label = fromHierarchy || fromApi || fromList;
                const tier = row.assigned_org?.tier ?? null;
                const tierColor: Record<string, 'warning' | 'default' | 'primary'> = {
                    bronze: 'warning',
                    silver: 'default',
                    gold: 'primary',
                };
                const tierLabel: Record<string, string> = {
                    bronze: 'Bronze',
                    silver: 'Silver',
                    gold: 'Gold',
                };
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            overflow: 'hidden',
                        }}
                    >
                        <Typography
                            variant="body2"
                            noWrap
                            title={label || undefined}
                            sx={{ flexShrink: 1, minWidth: 0 }}
                        >
                            {label || '--'}
                        </Typography>
                        {tier && (
                            <Chip
                                label={tierLabel[tier] ?? tier}
                                color={tierColor[tier] ?? 'default'}
                                size="small"
                                sx={{ flexShrink: 0, height: 18, fontSize: '0.65rem' }}
                            />
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'athlete_uuid',
            headerName: 'Athlete',
            width: 200,
            sortable: false,
            renderCell: (params) => {
                const row = params.row;
                const hAth = row.assigned_athlete;
                const uuid = hAth?.uuid || fp1EffectiveAthleteUuid(row);
                const name =
                    hAth?.name?.trim() ||
                    hAth?.email?.trim() ||
                    fp1EffectiveAthleteName(row)?.trim() ||
                    '';
                // Pod type logic — mirrors the Pods admin page:
                // "Transferred"       = athlete assigned AND the athlete holds the pod: either
                //                       pod.subscription_id IS NOT NULL (they purchased their own
                //                       ~$69.99/mo subscription) or the attachment was explicitly
                //                       performed as a transfer (athlete_transferred_at). The
                //                       second case covers a transfer to an athlete with no active
                //                       combo subscription — still a transfer, just unsubscribed.
                // "Assigned"          = athlete assigned with neither of the above
                //                       (org pays; athlete just gets app access).
                // "→ Transfer pending"= transfer request sent to an Individual but not yet
                //                       accepted (assigned_athlete not set yet).
                const isTransferred =
                    !!row.stripe_linked_subscription?.subscription_id ||
                    !!row.athlete_transferred_at;
                const statusLine = uuid
                    ? isTransferred
                        ? 'Transferred'
                        : 'Assigned'
                    : row.pending_transfer_to?.uuid
                    ? '→ Transfer pending'
                    : '';
                if (!uuid) {
                    return (
                        <Typography variant="body2" color="text.secondary">
                            --
                        </Typography>
                    );
                }
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        {fp1StripeSubscriptionNeedsAttention(row) && (
                            <Tooltip title="Stripe subscription is past due or unpaid">
                                <WarningAmberIcon color="warning" fontSize="small" />
                            </Tooltip>
                        )}
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" noWrap title={name || undefined}>
                                {name || '--'}
                            </Typography>
                            {statusLine ? (
                                <Typography
                                    variant="caption"
                                    color={
                                        statusLine === 'Transferred'
                                            ? 'success.main'
                                            : statusLine === '→ Transfer pending'
                                            ? 'warning.main'
                                            : 'text.secondary'
                                    }
                                    noWrap
                                >
                                    {statusLine}
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>
                );
            },
        },
        {
            // Optional column: upload sessions in the last 30 days (from workouts.uploads).
            field: 'usage_count_30d',
            headerName: 'Used (30d)',
            width: 110,
            renderCell: (params) => {
                const v = params.row.usage_count_30d;
                return (
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: v && v > 0 ? 600 : 400 }}
                        color={v == null ? 'text.secondary' : 'text.primary'}
                    >
                        {v == null ? '--' : v}
                    </Typography>
                );
            },
        },
        {
            // Optional column: most recent upload activity date.
            field: 'last_used_at',
            headerName: 'Last Used',
            width: 130,
            renderCell: (params) => formatDate(params.row.last_used_at ?? null),
        },
        {
            field: 'hardware_version',
            headerName: 'Hardware (PCB)',
            width: 130,
            sortable: false,
            renderCell: (params) => {
                const v = params.row.hardware_version?.trim() || params.row.model?.trim() || '';
                return (
                    <Typography variant="body2" noWrap title={v || undefined}>
                        {v || '--'}
                    </Typography>
                );
            },
        },
        {
            field: 'firmware_version',
            headerName: 'Firmware',
            width: 120,
            sortable: false,
            renderCell: (params) => {
                const v = params.row.firmware_version?.trim() || '';
                return (
                    <Typography variant="body2" noWrap title={v || undefined}>
                        {v || '--'}
                    </Typography>
                );
            },
        },
        {
            field: 'last_activity_at',
            headerName: 'Updated',
            width: 130,
            renderCell: (params) =>
                formatDate(params.row.last_activity_at ?? params.row.updated_at),
        },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            renderCell: (params) => (
                <Tooltip title="View Details">
                    <IconButton
                        size="small"
                        onClick={() => {
                            setSelectedDevice(params.row);
                            setDeviceDialogOpen(true);
                        }}
                    >
                        <OpenInNewIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    // Visible columns are grouped: all always-on default columns first (left), then the
    // optional columns the user toggled on + applied via Search (in toggle-list order),
    // then the edit/actions button pinned last. This keeps every default in one area and
    // every custom column in another.
    const isOptionalField = (field: string) => OPTIONAL_DEVICE_COLUMN_FIELDS.includes(field);
    const defaultDataColumns = allDeviceColumns.filter(
        (col) => !isOptionalField(col.field) && col.field !== 'actions',
    );
    const actionsColumn = allDeviceColumns.find((col) => col.field === 'actions');
    const appliedOptionalColumns = OPTIONAL_DEVICE_COLUMN_FIELDS.filter((f) =>
        appliedOptionalCols.includes(f),
    )
        .map((f) => allDeviceColumns.find((col) => col.field === f))
        .filter((col): col is GridColDef<Fp1Device> => Boolean(col));
    const deviceColumns: GridColDef<Fp1Device>[] = [
        ...defaultDataColumns,
        ...appliedOptionalColumns,
        ...(actionsColumn ? [actionsColumn] : []),
    ];

    const shipmentColumns: GridColDef<Fp1Shipment>[] = [
        { field: 'supplier_name', headerName: 'Supplier', width: 200, sortable: false },
        { field: 'po_number', headerName: 'PO Number', width: 150, sortable: false },
        {
            field: 'received_at',
            headerName: 'Received',
            width: 140,
            renderCell: (params) => formatDate(params.value),
        },
        { field: 'received_by_name', headerName: 'Received By', width: 160, sortable: false },
        {
            field: 'total_cost_cents',
            headerName: 'Total Cost',
            width: 120,
            sortable: false,
            renderCell: (params) => formatCost(params.value),
        },
        {
            field: 'notes',
            headerName: 'Notes',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary" noWrap>
                    {params.value || '--'}
                </Typography>
            ),
        },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            renderCell: (params) => (
                <Tooltip title="View Shipment">
                    <IconButton
                        size="small"
                        onClick={() => {
                            setShipmentDetailUuid(params.row.uuid);
                            setShipmentDetailOpen(true);
                        }}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    const gridSx = {
        border: 'none',
        '& .MuiDataGrid-cell:focus': { outline: 'none' },
        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
        '& .MuiDataGrid-columnHeaders': {
            bgcolor: 'action.hover',
            borderRadius: 0,
        },
        '& .MuiDataGrid-row:hover': {
            bgcolor: (t: any) => alpha(t.palette.primary.main, 0.04),
        },
    };

    return (
        <Box sx={{ p: 2.5 }}>
            <Snackbar
                open={!!snackMessage}
                autoHideDuration={5000}
                onClose={() => setSnackMessage('')}
                message={snackMessage}
            />

            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    FP1 Hardware Inventory
                </Typography>
            </Box>

            {/* Sub-tabs */}
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                    mb: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '& .MuiTabs-indicator': { height: 3, borderRadius: 1.5 },
                }}
            >
                <SubTab
                    icon={
                        <Badge
                            badgeContent={pendingOrders}
                            color="error"
                            sx={{
                                '& .MuiBadge-badge': {
                                    fontSize: '0.5rem',
                                    height: '14px',
                                    minWidth: '14px',
                                    padding: '0 4px',
                                },
                            }}
                        >
                            <DescriptionIcon sx={{ fontSize: '1.1rem' }} />
                        </Badge>
                    }
                    iconPosition="start"
                    label="Orders"
                />
                <SubTab
                    icon={<AssessmentIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Management"
                />
                <SubTab
                    icon={<BusinessIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Orgs"
                />
                <SubTab
                    icon={<InventoryIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Devices"
                />
                <SubTab
                    icon={<LocalShippingIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Shipments"
                />
                <SubTab
                    icon={<InventoryIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Returns"
                />
                <SubTab
                    icon={<BatteryAlertIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Device Health"
                />
                <SubTab
                    icon={<GavelIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Invoices"
                />
                <SubTab
                    icon={<CreditCardIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Sub Tracking"
                />
                <SubTab
                    icon={<BugReportIcon sx={{ fontSize: '1.1rem' }} />}
                    iconPosition="start"
                    label="Debugging"
                />
            </Tabs>

            {/* ═══ TAB 0: Orders — Board (kanban) + Table views ═══ */}
            {activeTab === 0 && (
                <Box sx={{ mx: -2.5 }}>
                    <Fp1OrdersSection />
                </Box>
            )}

            {/* ═══ TAB 1: Management (business summary, read-only) ═══ */}
            {activeTab === 1 && <Fp1ManagementTab />}

            {/* ═══ TAB 2: Org Pods (pod distribution by organization) ═══ */}
            {activeTab === 2 && (
                <Fp1OrgPodsTab
                    onViewOrgDevices={handleViewOrgDevices}
                    onViewOrgAthleteSubs={handleViewOrgAthleteSubs}
                />
            )}

            {/* ═══ TAB 3: Devices (Hardware) ═══ */}
            {activeTab === 3 && (
                <>
                    {/* Summary cards (left) + optional column toggles (right) */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 2,
                            mb: 2.5,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <SummaryCard
                                label="Total Devices"
                                value={deviceStatusCounts.total}
                                icon={
                                    <InventoryIcon
                                        sx={{
                                            color: theme.palette.primary.main,
                                            fontSize: '1.5rem',
                                        }}
                                    />
                                }
                                color={theme.palette.primary.main}
                            />
                            {DEVICE_STATUS_SUMMARY.map(({ key, label }) => (
                                <SummaryCard
                                    key={key}
                                    label={label}
                                    value={deviceStatusCounts[key]}
                                    color={deviceStatusSummaryAccent(theme, key)}
                                />
                            ))}
                        </Box>

                        {/* Optional column toggles — apply on Search */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.75,
                                ml: 'auto',
                                minWidth: 220,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        lineHeight: 1,
                                        fontWeight: 700,
                                    }}
                                >
                                    <ViewColumnIcon sx={{ fontSize: '1.05rem' }} />
                                    Show columns
                                </Typography>
                                {optionalColsDirty && (
                                    <Typography
                                        variant="caption"
                                        color="warning.main"
                                        sx={{ fontWeight: 700 }}
                                    >
                                        Click Search to apply
                                    </Typography>
                                )}
                            </Box>
                            <ToggleButtonGroup
                                value={pendingOptionalCols}
                                onChange={(_, vals) => setPendingOptionalCols(vals as string[])}
                                size="small"
                                sx={{
                                    flexWrap: 'wrap',
                                    gap: 0.75,
                                    '& .MuiToggleButton-root': {
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: '8px !important',
                                        ml: '0 !important',
                                        border: (t) => `1px solid ${t.palette.grey[400]}`,
                                        bgcolor: 'background.paper',
                                        color: 'text.primary',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                        '&:hover': {
                                            bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                                            borderColor: 'primary.main',
                                        },
                                        '&.Mui-selected': {
                                            bgcolor: 'primary.main',
                                            color: 'primary.contrastText',
                                            borderColor: 'primary.main',
                                            boxShadow: (t) =>
                                                `0 1px 3px ${alpha(t.palette.primary.main, 0.5)}`,
                                            '&:hover': { bgcolor: 'primary.dark' },
                                        },
                                    },
                                }}
                            >
                                {OPTIONAL_DEVICE_COLUMNS.map((c) => (
                                    <ToggleButton key={c.field} value={c.field}>
                                        {c.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Box>
                    </Box>

                    {/* Filters */}
                    <FilterBar onSubmit={handleDeviceSearch}>
                        <TextField
                            size="small"
                            label="Serial Number"
                            value={deviceSerialSearch}
                            onChange={(e) => setDeviceSerialSearch(e.target.value)}
                            sx={{ width: 200 }}
                            placeholder="Search serial..."
                        />
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={deviceTypeFilter}
                                onChange={(e) => setDeviceTypeFilter(e.target.value)}
                                label="Type"
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="fp1">FP1</MenuItem>
                                <MenuItem value="fp1_gateway">Gateway (FP1)</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 170 }}>
                            <InputLabel>FP1 status</InputLabel>
                            <Select
                                value={deviceStatusFilter}
                                onChange={(e) => setDeviceStatusFilter(e.target.value)}
                                label="FP1 status"
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="in_stock">In Stock</MenuItem>
                                <MenuItem value="deployed">Deployed</MenuItem>
                                <MenuItem value="maintenance">Maintenance</MenuItem>
                                <MenuItem value="defective">Defective</MenuItem>
                                <MenuItem value="unknown">Unknown</MenuItem>
                                <MenuItem value="decommissioned">Decommissioned</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 170 }}>
                            <InputLabel>Sub. Status</InputLabel>
                            <Select
                                value={deviceSubscriptionFilter}
                                onChange={(e) => setDeviceSubscriptionFilter(e.target.value)}
                                label="Sub. Status"
                            >
                                <MenuItem value="">All</MenuItem>
                                {(
                                    [
                                        ['na', 'N/A'],
                                        ['comp', 'Comp'],
                                        ['invoiced', 'Invoiced'],
                                        ['active_athlete', 'Active Athlete'],
                                        ['active_org', 'Active Org'],
                                        ['partial_org', 'Partial Org'],
                                        ['past_due', 'Past Due'],
                                        ['no_sub', 'No Sub'],
                                    ] as [keyof NonNullable<typeof deviceSubStatusCounts>, string][]
                                ).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>
                                        {label}
                                        {deviceSubStatusCounts != null
                                            ? ` (${deviceSubStatusCounts[key]})`
                                            : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Holder</InputLabel>
                            <Select
                                value={deviceHolderFilter}
                                onChange={(e) => setDeviceHolderFilter(e.target.value)}
                                label="Holder"
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="athlete">Athlete-held (Transferred)</MenuItem>
                                <MenuItem value="org">Org-held</MenuItem>
                            </Select>
                        </FormControl>
                        <Autocomplete
                            value={
                                organizations.find((o) => o.uuid === deviceOrganizationFilter) ||
                                null
                            }
                            onChange={(_, v) => setDeviceOrganizationFilter(v?.uuid || '')}
                            onInputChange={(_, val, reason) => {
                                if (reason !== 'reset') fetchOrganizations(val);
                            }}
                            getOptionKey={(o) => o.uuid}
                            filterOptions={(options) => options}
                            isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
                            size="small"
                            options={organizations}
                            getOptionLabel={(o) => orgOptionLabel(o)}
                            renderInput={(params) => (
                                <TextField {...params} label="Organization" placeholder="All" />
                            )}
                            sx={{ minWidth: 260, maxWidth: 360 }}
                        />
                        <TextField
                            size="small"
                            label="Athlete name / email"
                            value={deviceAthleteSearch}
                            onChange={(e) => setDeviceAthleteSearch(e.target.value)}
                            sx={{ minWidth: 220, maxWidth: 320 }}
                            placeholder="Search athlete..."
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SearchIcon />}
                            size="medium"
                        >
                            Search
                        </Button>
                        <Button
                            type="button"
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            size="medium"
                            disabled={exportingDevices}
                            onClick={handleExportDevices}
                        >
                            {exportingDevices ? 'Exporting…' : 'Download CSV'}
                        </Button>
                    </FilterBar>

                    {/* Top pagination row */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 0.5,
                            mb: 1,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            {deviceFilteredRowCount === 0
                                ? '0 results'
                                : `${
                                      devicePagination.page * devicePagination.pageSize + 1
                                  }–${Math.min(
                                      (devicePagination.page + 1) * devicePagination.pageSize,
                                      deviceFilteredRowCount,
                                  )} of ${deviceFilteredRowCount}`}
                        </Typography>
                        <IconButton
                            size="small"
                            disabled={devicePagination.page === 0}
                            onClick={() =>
                                handleDevicePaginationChange({
                                    ...devicePagination,
                                    page: devicePagination.page - 1,
                                })
                            }
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            disabled={
                                (devicePagination.page + 1) * devicePagination.pageSize >=
                                deviceFilteredRowCount
                            }
                            onClick={() =>
                                handleDevicePaginationChange({
                                    ...devicePagination,
                                    page: devicePagination.page + 1,
                                })
                            }
                        >
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Grid */}
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <DataGridPremium
                            rows={devices}
                            columns={deviceColumns}
                            getRowId={(row) => row.uuid}
                            getRowClassName={(params) =>
                                fp1StripeSubscriptionNeedsAttention(params.row)
                                    ? 'fp1-grid-row-sub-warning'
                                    : ''
                            }
                            rowCount={deviceFilteredRowCount}
                            pagination
                            paginationMode="server"
                            paginationModel={devicePagination}
                            onPaginationModelChange={handleDevicePaginationChange}
                            pageSizeOptions={[10, 25, 50, 100]}
                            disableColumnMenu
                            checkboxSelection={false}
                            autoHeight
                            sx={{
                                ...gridSx,
                                '& .fp1-grid-row-sub-warning': {
                                    bgcolor: (t) => alpha(t.palette.warning.main, 0.14),
                                },
                            }}
                            onRowClick={(params) => {
                                setSelectedDevice(params.row);
                                setDeviceDialogOpen(true);
                            }}
                        />
                    </Paper>
                </>
            )}

            {/* ═══ TAB 5: Returns ═══ */}
            {activeTab === 5 && <Fp1ReturnsTab />}

            {/* ═══ TAB 4: Shipments ═══ */}
            {activeTab === 4 && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <SummaryCard
                                label="Total Shipments"
                                value={totalShipments}
                                icon={
                                    <LocalShippingIcon
                                        sx={{ color: theme.palette.info.main, fontSize: '1.5rem' }}
                                    />
                                }
                                color={theme.palette.info.main}
                            />
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setReceiveShipmentOpen(true)}
                        >
                            Receive Shipment
                        </Button>
                    </Box>

                    <FilterBar onSubmit={handleShipmentSearch}>
                        <TextField
                            size="small"
                            label="Supplier Name"
                            value={shipmentSupplierSearch}
                            onChange={(e) => setShipmentSupplierSearch(e.target.value)}
                            sx={{ width: 250 }}
                            placeholder="Search supplier..."
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SearchIcon />}
                            size="medium"
                        >
                            Search
                        </Button>
                    </FilterBar>

                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <DataGridPremium
                            rows={shipments}
                            columns={shipmentColumns}
                            getRowId={(row) => row.uuid}
                            rowCount={totalShipments}
                            pagination
                            paginationMode="server"
                            paginationModel={shipmentPagination}
                            onPaginationModelChange={handleShipmentPaginationChange}
                            pageSizeOptions={[10, 25, 50]}
                            disableColumnMenu
                            checkboxSelection={false}
                            autoHeight
                            sx={gridSx}
                        />
                    </Paper>
                </>
            )}

            {/* ═══ TAB 6: Device Health ═══ */}
            {activeTab === 6 && <Fp1BatteryHealthTab />}

            {/* ═══ TAB 7: Contracts ═══ */}
            {activeTab === 7 && <Fp1ContractsTab />}

            {/* ═══ TAB 8: Sub Tracking ═══ */}
            {activeTab === 8 && <Fp1SubTrackingTab />}

            {/* ═══ TAB 9: Debugging ═══ */}
            {activeTab === 9 && <Fp1DebuggingTab />}

            {/* ── Dialogs ─────────────────────────────────────────────────── */}
            <Fp1DeviceDetailDialog
                open={deviceDialogOpen}
                device={selectedDevice}
                onClose={() => setDeviceDialogOpen(false)}
                onSave={() => {
                    setDeviceDialogOpen(false);
                    fetchDevices(deviceParams);
                    fetchDeviceStatusCounts();
                    setSnackMessage('Device updated');
                }}
            />
            <Fp1ReceiveShipmentDialog
                open={receiveShipmentOpen}
                onClose={() => setReceiveShipmentOpen(false)}
                onSuccess={() => {
                    setReceiveShipmentOpen(false);
                    fetchShipments();
                    fetchDevices(deviceParams);
                    fetchDeviceStatusCounts();
                    setSnackMessage('Shipment received successfully');
                }}
            />
            <Fp1ShipmentDetailDialog
                open={shipmentDetailOpen}
                shipmentUuid={shipmentDetailUuid}
                onClose={() => setShipmentDetailOpen(false)}
            />
        </Box>
    );
};

export default Fp1Page;
